import React, { createContext, useContext, useState, ReactNode } from 'react';

// Типы для состояния анимаций
export interface AnimationState {
  isDiceRolling: boolean;
  isMoving: boolean;
  isSpecialEffect: boolean;
  currentStage: string;
  fromCell: number | null;
  toCell: number | null;
  currentStep: number;
  diceResult: any;
  effectType: 'snake' | 'ladder' | null;
}

// Контекст для управления анимациями
interface GameAnimationContextType {
  animationState: AnimationState;
  setAnimationState: (state: Partial<AnimationState>) => void;
  startDiceAnimation: (onComplete: (result: any) => void, apiCall: () => Promise<any>) => Promise<void>;
  startMovementAnimation: (from: number, to: number, steps?: number[]) => Promise<void>;
  startSpecialEffect: (type: 'snake' | 'ladder', from: number, to: number) => Promise<void>;
  resetAnimation: () => void;
}

const GameAnimationContext = createContext<GameAnimationContextType | undefined>(undefined);

// Начальное состояние
const initialState: AnimationState = {
  isDiceRolling: false,
  isMoving: false,
  isSpecialEffect: false,
  currentStage: '',
  fromCell: null,
  toCell: null,
  currentStep: 0,
  diceResult: null,
  effectType: null,
};

// Провайдер контекста анимаций
export const GameAnimationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [animationState, setAnimationStateInternal] = useState<AnimationState>(initialState);

  const setAnimationState = (newState: Partial<AnimationState>) => {
    setAnimationStateInternal(prev => ({ ...prev, ...newState }));
  };

  // Запуск анимации кубика с правильной логикой
  const startDiceAnimation = async (onComplete: (result: any) => void, apiCall: () => Promise<any>) => {
    setAnimationState({
      isDiceRolling: true,
      currentStage: 'rolling',
      diceResult: null
    });

    // ЭТАП 1: Бросок кубика (1000ms = 1 секунда)
    setAnimationState({ currentStage: 'rolling' });
    
    // Запускаем API параллельно с анимацией
    const apiPromise = apiCall();
    
    // Ждем 1 секунду анимации
    await new Promise(resolve => setTimeout(resolve, 1000));

    // ЭТАП 2: Ждем API результат и сразу показываем
    try {
      const apiResult = await apiPromise;
      console.log('Dice result:', apiResult.dice_roll);
      
      // Показываем результат кубика
      setAnimationState({ 
        currentStage: 'result_shown',
        diceResult: apiResult 
      });
      
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Завершаем анимацию - игра продолжается
      setAnimationState({ isDiceRolling: false });
      onComplete(apiResult);
      
    } catch (error) {
      console.error('API call failed:', error);
      setAnimationState({ 
        isDiceRolling: false,
        currentStage: 'error' 
      });
      throw error;
    }
  };

  // Анимация движения по клеткам
  const startMovementAnimation = async (from: number, to: number, steps?: number[]) => {
    setAnimationState({
      isMoving: true,
      fromCell: from,
      toCell: to,
      currentStep: 0
    });

    // Генерируем путь движения если не передан
    const movementSteps = steps || generateMovementPath(from, to);
    
    for (let i = 0; i < movementSteps.length; i++) {
      setAnimationState({ currentStep: i });
      await new Promise(resolve => setTimeout(resolve, 300)); // 300мс на шаг
    }

    setAnimationState({ isMoving: false });
  };

  // Спецэффекты для змей и лестниц
  const startSpecialEffect = async (type: 'snake' | 'ladder', from: number, to: number) => {
    setAnimationState({
      isSpecialEffect: true,
      effectType: type,
      fromCell: from,
      toCell: to
    });

    // Длительность спецэффекта
    const duration = type === 'snake' ? 2000 : 2500; // змеи быстрее лестниц
    await new Promise(resolve => setTimeout(resolve, duration));

    setAnimationState({ isSpecialEffect: false });
  };

  // Сброс всех анимаций
  const resetAnimation = () => {
    setAnimationStateInternal(initialState);
  };

  // Генерация пути движения между клетками
  const generateMovementPath = (from: number, to: number): number[] => {
    const path: number[] = [];
    const direction = to > from ? 1 : -1;
    
    for (let cell = from + direction; direction > 0 ? cell <= to : cell >= to; cell += direction) {
      path.push(cell);
    }
    
    return path;
  };

  const contextValue: GameAnimationContextType = {
    animationState,
    setAnimationState,
    startDiceAnimation,
    startMovementAnimation,
    startSpecialEffect,
    resetAnimation,
  };

  return (
    <GameAnimationContext.Provider value={contextValue}>
      {children}
    </GameAnimationContext.Provider>
  );
};

// Хук для использования контекста анимаций
export const useGameAnimation = (): GameAnimationContextType => {
  const context = useContext(GameAnimationContext);
  if (!context) {
    throw new Error('useGameAnimation must be used within GameAnimationProvider');
  }
  return context;
};
